/*
  Pixelpa — Photoshop-style "New Document" modal.

  Self-contained UI component. Construct once; call `open(initial?)` to show.
  Resolves with the chosen document spec on Create / Enter, or `null` on
  Cancel / Escape.  Outside-click is intentionally ignored (PS modal behaviour).

  Width/height are entered in the user's choice of unit (px / % / in / cm /
  mm / points / picas) and converted to *pixels* in the resolved result
  using the chosen DPI.

  Unit-conversion helpers (`unitToPixels`, `pixelsToUnit`) are exported pure
  for tests — no DOM dependency.

  #142: 8 preset categories (Recent, Saved, Photo, Print, Art & Illustration,
  Web, Mobile, Film & Video) with thumbnail cards, Advanced disclosure for
  Color Profile + Pixel Aspect Ratio, Artboards checkbox, Color Mode
  dropdown (Bitmap / Grayscale / RGB / CMYK / Lab), Bit Depth (8/16/32).
  Recent presets are persisted to localStorage.

  #248: Save Preset (Saved tab localStorage round-trip), resolution-unit
  dropdown (Pixels/Inch ↔ Pixels/Centimeter with live conversion), W/H
  orientation-swap button, mode-aware bit-depth options (Bitmap → 1-bit only;
  CMYK/Lab → hide 32-bit), backgroundLocked flag for white/colour backgrounds,
  outside-click no longer closes the dialog.
*/

import { ColorPicker, trapFocus, type RGB } from "./ColorPicker";

// ---------- Public types ----------

export type Unit = "px" | "%" | "in" | "cm" | "mm" | "pt" | "pc";
export type Category =
  | "Recent"
  | "Saved"
  | "Photo"
  | "Print"
  | "Art & Illustration"
  | "Web"
  | "Mobile"
  | "Film & Video";
export type ColorMode = "bitmap" | "grayscale" | "rgb" | "cmyk" | "lab";
export type BitDepth = 1 | 8 | 16 | 32;
export type ResolutionUnit = "ppi" | "ppcm";
export type Background =
  | "white"
  | "black"
  | "transparent"
  | { r: number; g: number; b: number };

export interface NewDocumentResult {
  name: string;
  width: number;   // pixels
  height: number;  // pixels
  dpi: number;
  background: Background;
  /** true when background is white or a solid colour — PS locks the background layer */
  backgroundLocked: boolean;
  colorMode: ColorMode;
  bitDepth: BitDepth;
  colorProfile: string;
  pixelAspectRatio: number;
  artboards: number;       // 0 if disabled
}

// ---------- Pure helpers (exported for tests) ----------

const CM_PER_INCH = 2.54;
const MM_PER_INCH = 25.4;
const POINTS_PER_INCH = 72;
const PICAS_PER_INCH = 6;

export function unitToPixels(value: number, unit: Unit, dpi: number, referencePx = 0): number {
  if (!Number.isFinite(value) || !Number.isFinite(dpi) || dpi <= 0) return 0;
  switch (unit) {
    case "px": return value;
    case "%": return Number.isFinite(referencePx) && referencePx > 0 ? (value / 100) * referencePx : 0;
    case "in": return value * dpi;
    case "cm": return (value / CM_PER_INCH) * dpi;
    case "mm": return (value / MM_PER_INCH) * dpi;
    case "pt": return (value / POINTS_PER_INCH) * dpi;
    case "pc": return (value / PICAS_PER_INCH) * dpi;
  }
}

export function pixelsToUnit(px: number, unit: Unit, dpi: number, referencePx = 0): number {
  if (!Number.isFinite(px) || !Number.isFinite(dpi) || dpi <= 0) return 0;
  switch (unit) {
    case "px": return px;
    case "%": return Number.isFinite(referencePx) && referencePx > 0 ? (px / referencePx) * 100 : 0;
    case "in": return px / dpi;
    case "cm": return (px / dpi) * CM_PER_INCH;
    case "mm": return (px / dpi) * MM_PER_INCH;
    case "pt": return (px / dpi) * POINTS_PER_INCH;
    case "pc": return (px / dpi) * PICAS_PER_INCH;
  }
}

// ---------- Preset catalog ----------

export interface Preset {
  name: string;
  // Stored in the preset's native unit; converted to pixels on apply.
  width: number;
  height: number;
  unit: Unit;
  dpi: number;
}

const STATIC_PRESETS: Record<Exclude<Category, "Recent" | "Saved">, Preset[]> = {
  Photo: [
    { name: "Default Photoshop Size", width: 7,    height: 5,    unit: "in", dpi: 300 },
    { name: "Landscape, 4 x 6",       width: 6,    height: 4,    unit: "in", dpi: 300 },
    { name: "Portrait, 5 x 7",        width: 5,    height: 7,    unit: "in", dpi: 300 },
    { name: "Landscape, 8 x 10",      width: 10,   height: 8,    unit: "in", dpi: 300 },
    { name: "High-Res 4K",            width: 3840, height: 2160, unit: "px", dpi: 300 },
    { name: "Square 2048",            width: 2048, height: 2048, unit: "px", dpi: 300 },
  ],
  Print: [
    { name: "US Letter",              width: 8.5,  height: 11,   unit: "in", dpi: 300 },
    { name: "Legal",                  width: 8.5,  height: 14,   unit: "in", dpi: 300 },
    { name: "Tabloid",                width: 11,   height: 17,   unit: "in", dpi: 300 },
    { name: "A4",                     width: 210,  height: 297,  unit: "mm", dpi: 300 },
    { name: "A3",                     width: 297,  height: 420,  unit: "mm", dpi: 300 },
    { name: "Postcard 4 x 6",         width: 6,    height: 4,    unit: "in", dpi: 300 },
  ],
  "Art & Illustration": [
    { name: "Default Art Board",      width: 1920, height: 1080, unit: "px", dpi: 300 },
    { name: "Square Print 12 in",     width: 12,   height: 12,   unit: "in", dpi: 300 },
    { name: "Comic Page",             width: 6.625, height: 10.187, unit: "in", dpi: 600 },
    { name: "Manga Page",             width: 5.83, height: 8.27, unit: "in", dpi: 600 },
    { name: "Illustration A4",        width: 210,  height: 297,  unit: "mm", dpi: 600 },
    { name: "Concept Wide",           width: 3000, height: 1500, unit: "px", dpi: 300 },
  ],
  Web: [
    { name: "1920 x 1080",            width: 1920, height: 1080, unit: "px", dpi: 72 },
    { name: "1366 x 768",             width: 1366, height: 768,  unit: "px", dpi: 72 },
    { name: "1440 x 900",             width: 1440, height: 900,  unit: "px", dpi: 72 },
    { name: "1280 x 800",             width: 1280, height: 800,  unit: "px", dpi: 72 },
    { name: "Common Banner 1500 x 500", width: 1500, height: 500, unit: "px", dpi: 72 },
    { name: "Social Square 1080",     width: 1080, height: 1080, unit: "px", dpi: 72 },
  ],
  Mobile: [
    { name: "iPhone 16 Pro",          width: 1206, height: 2622, unit: "px", dpi: 72 },
    { name: "iPhone 16",              width: 1179, height: 2556, unit: "px", dpi: 72 },
    { name: "iPad Pro 13\"",          width: 2064, height: 2752, unit: "px", dpi: 72 },
    { name: "Pixel 9",                width: 1080, height: 2424, unit: "px", dpi: 72 },
    { name: "Generic Portrait",       width: 1080, height: 1920, unit: "px", dpi: 72 },
    { name: "Android Tablet",         width: 1600, height: 2560, unit: "px", dpi: 72 },
  ],
  "Film & Video": [
    { name: "HDTV 1080p",             width: 1920, height: 1080, unit: "px", dpi: 72 },
    { name: "UHD 4K",                 width: 3840, height: 2160, unit: "px", dpi: 72 },
    { name: "Cinema DCP 2K",          width: 2048, height: 1080, unit: "px", dpi: 72 },
    { name: "Cinema DCP 4K",          width: 4096, height: 2160, unit: "px", dpi: 72 },
    { name: "NTSC DV",                width: 720,  height: 480,  unit: "px", dpi: 72 },
    { name: "PAL DV",                 width: 720,  height: 576,  unit: "px", dpi: 72 },
  ],
};

export const CATEGORIES: Category[] = [
  "Recent",
  "Saved",
  "Photo",
  "Print",
  "Art & Illustration",
  "Web",
  "Mobile",
  "Film & Video",
];

const RECENT_STORAGE_KEY = "pixelpa.newdoc.recent";
const SAVED_STORAGE_KEY = "pixelpa.newdoc.saved";
const RECENT_LIMIT = 10;

type BgChoice = "white" | "black" | "transparent" | "custom";

// ---------- Recent / Saved persistence (exported for tests) ----------

export function loadRecent(storage: Storage | null = typeof localStorage !== "undefined" ? localStorage : null): Preset[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(RECENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, RECENT_LIMIT).filter(isPreset);
  } catch {
    return [];
  }
}

export function loadSaved(storage: Storage | null = typeof localStorage !== "undefined" ? localStorage : null): Preset[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(SAVED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPreset);
  } catch {
    return [];
  }
}

export function savePreset(preset: Preset, storage: Storage | null = typeof localStorage !== "undefined" ? localStorage : null): void {
  if (!storage) return;
  const list = loadSaved(storage);
  // Dedupe by name — replace existing entry with same name
  const filtered = list.filter((p) => p.name !== preset.name);
  filtered.unshift(preset);
  try {
    storage.setItem(SAVED_STORAGE_KEY, JSON.stringify(filtered));
  } catch {
    /* ignore quota errors */
  }
}

export function deleteSavedPreset(name: string, storage: Storage | null = typeof localStorage !== "undefined" ? localStorage : null): void {
  if (!storage) return;
  const list = loadSaved(storage).filter((p) => p.name !== name);
  try {
    storage.setItem(SAVED_STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

export function addRecent(preset: Preset, storage: Storage | null = typeof localStorage !== "undefined" ? localStorage : null): void {
  if (!storage) return;
  const list = loadRecent(storage);
  // Dedupe by name+dim+unit
  const filtered = list.filter((p) =>
    !(p.name === preset.name && p.width === preset.width && p.height === preset.height && p.unit === preset.unit));
  filtered.unshift(preset);
  try {
    storage.setItem(RECENT_STORAGE_KEY, JSON.stringify(filtered.slice(0, RECENT_LIMIT)));
  } catch {
    /* ignore */
  }
}

function isPreset(x: unknown): x is Preset {
  if (typeof x !== "object" || x === null) return false;
  const o = x as Record<string, unknown>;
  return typeof o.name === "string" &&
    Number.isFinite(o.width) &&
    Number.isFinite(o.height) &&
    typeof o.unit === "string" &&
    Number.isFinite(o.dpi);
}

// ---------- DOM helpers ----------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function fmt(n: number, unit: Unit): string {
  // px is integer; other units get up to 2 decimals.
  if (unit === "px") return String(Math.round(n));
  const fixed = n.toFixed(2);
  return fixed.replace(/\.?0+$/, "");
}

// ---------- Component ----------

export class NewDocumentDialog {
  private built = false;
  private root!: HTMLDivElement;
  private sheet!: HTMLDivElement;

  // Tabs
  private tabEls = new Map<Category, HTMLButtonElement>();

  // Preset thumbnails area
  private thumbsHost!: HTMLDivElement;
  private thumbCards: HTMLDivElement[] = [];

  // Fields
  private nameInput!: HTMLInputElement;
  private widthInput!: HTMLInputElement;
  private heightInput!: HTMLInputElement;
  private unitSelect!: HTMLSelectElement;
  private swapOrientationBtn!: HTMLButtonElement;
  private dpiInput!: HTMLInputElement;
  private resUnitSelect!: HTMLSelectElement;
  private colorModeSelect!: HTMLSelectElement;
  private bitDepthSelect!: HTMLSelectElement;
  private bgSelect!: HTMLSelectElement;
  private bgSwatch!: HTMLDivElement;
  private savePresetBtn!: HTMLButtonElement;
  private savePresetNameInput!: HTMLInputElement;

  // Advanced
  private advancedHost!: HTMLDivElement;
  private advancedToggle!: HTMLButtonElement;
  private colorProfileSelect!: HTMLSelectElement;
  private pixelAspectInput!: HTMLInputElement;
  private artboardsCheck!: HTMLInputElement;
  private artboardsCount!: HTMLInputElement;

  private btnCreate!: HTMLButtonElement;
  private btnCancel!: HTMLButtonElement;

  // State (source of truth — pixels)
  private category: Category = "Photo";
  private widthPx = 0;
  private heightPx = 0;
  private dpi = 300;
  private resUnit: ResolutionUnit = "ppi";
  private unit: Unit = "in";
  private bgChoice: BgChoice = "white";
  private customColor: RGB = { r: 255, g: 255, b: 255 };
  private untitledCounter = 1;
  private colorMode: ColorMode = "rgb";
  private bitDepth: BitDepth = 8;
  private colorProfile = "sRGB IEC61966-2.1";
  private pixelAspectRatio = 1;
  private artboardsOn = false;
  private artboardsCountVal = 1;
  private selectedPresetIdx = 0;

  // Reused color picker for "Custom" background.
  private colorPicker: ColorPicker | null = null;

  // Modal lifecycle
  private resolveFn: ((value: NewDocumentResult | null) => void) | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  private outsideHandler: ((e: MouseEvent) => void) | null = null;
  private openerEl: Element | null = null;

  constructor() {
    // Lazy build — DOM construction deferred until first open().
  }

  // ---------- Public API ----------

  open(initial?: Partial<NewDocumentResult>): Promise<NewDocumentResult | null> {
    if (!this.built) this.build();

    if (this.resolveFn) {
      const prev = this.resolveFn;
      this.resolveFn = null;
      prev(null);
    }

    // Remember opener for focus restoration on close (#47).
    this.openerEl = document.activeElement;

    // Seed state from initial or from the current category's first preset.
    const presets = this.presetsFor(this.category);
    const firstPreset = presets[0];
    if (firstPreset) {
      const seedDpi = initial?.dpi ?? firstPreset.dpi;
      this.dpi = seedDpi;
      this.unit = firstPreset.unit;
      if (typeof initial?.width === "number" && typeof initial?.height === "number") {
        this.widthPx = Math.max(1, Math.round(initial.width));
        this.heightPx = Math.max(1, Math.round(initial.height));
      } else {
        this.widthPx = Math.round(unitToPixels(firstPreset.width, firstPreset.unit, seedDpi));
        this.heightPx = Math.round(unitToPixels(firstPreset.height, firstPreset.unit, seedDpi));
      }
    } else {
      this.dpi = initial?.dpi ?? 300;
      this.unit = "px";
      this.widthPx = Math.max(1, Math.round(initial?.width ?? 1920));
      this.heightPx = Math.max(1, Math.round(initial?.height ?? 1080));
    }

    if (initial?.background !== undefined) {
      if (initial.background === "white" || initial.background === "black" || initial.background === "transparent") {
        this.bgChoice = initial.background;
      } else {
        this.bgChoice = "custom";
        this.customColor = {
          r: Math.round(initial.background.r),
          g: Math.round(initial.background.g),
          b: Math.round(initial.background.b),
        };
      }
    } else {
      this.bgChoice = "white";
    }

    if (initial?.colorMode) this.colorMode = initial.colorMode;
    if (initial?.bitDepth) this.bitDepth = initial.bitDepth;
    if (initial?.colorProfile) this.colorProfile = initial.colorProfile;
    if (Number.isFinite(initial?.pixelAspectRatio)) this.pixelAspectRatio = initial!.pixelAspectRatio!;
    if (typeof initial?.artboards === "number" && initial.artboards > 0) {
      this.artboardsOn = true;
      this.artboardsCountVal = Math.max(1, Math.round(initial.artboards));
    }

    this.nameInput.value = initial?.name ?? `Untitled-${this.untitledCounter}`;
    this.selectedPresetIdx = 0;

    this.refreshAll();
    this.root.style.display = "";

    setTimeout(() => {
      this.nameInput.focus();
      this.nameInput.select();
    }, 0);

    return new Promise<NewDocumentResult | null>((resolve) => {
      this.resolveFn = resolve;
    });
  }

  close(): void {
    this.finish(null);
  }

  destroy(): void {
    if (this.keyHandler) window.removeEventListener("keydown", this.keyHandler, true);
    this.keyHandler = null;
    if (this.outsideHandler && this.built) {
      this.root.removeEventListener("mousedown", this.outsideHandler);
    }
    this.outsideHandler = null;
    if (this.built && this.root.parentNode) this.root.parentNode.removeChild(this.root);
    if (this.colorPicker) {
      this.colorPicker.destroy();
      this.colorPicker = null;
    }
    this.built = false;
  }

  // ---------- Preset lookup ----------

  private presetsFor(cat: Category): Preset[] {
    if (cat === "Recent") return loadRecent();
    if (cat === "Saved") return loadSaved();
    return STATIC_PRESETS[cat];
  }

  // ---------- Build (lazy) ----------

  private build(): void {
    this.root = el("div", "new-doc");
    this.root.style.display = "none";

    // ARIA: modal dialog accessibility (#47)
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "true");
    this.root.setAttribute("aria-labelledby", "new-doc-title");

    this.sheet = el("div", "new-doc__sheet");
    this.root.appendChild(this.sheet);

    // Title bar
    const title = el("div", "new-doc__title");
    title.id = "new-doc-title";
    title.textContent = "New Document";
    this.sheet.appendChild(title);

    // Category tabs across the top
    const tabs = el("div", "new-doc__tabs");
    for (const cat of CATEGORIES) {
      const btn = el("button", "new-doc__tab");
      btn.type = "button";
      btn.textContent = cat;
      btn.dataset.category = cat;
      btn.addEventListener("click", () => {
        if (this.category === cat) return;
        this.category = cat;
        this.selectedPresetIdx = 0;
        const presets = this.presetsFor(cat);
        if (presets[0]) this.applyPreset(presets[0]);
        this.refreshAll();
      });
      tabs.appendChild(btn);
      this.tabEls.set(cat, btn);
    }
    this.sheet.appendChild(tabs);

    // Body: presets thumbnails + fields
    const body = el("div", "new-doc__body");

    // Presets column with thumbnails
    const presetsCol = el("div", "new-doc__presets");
    const presetLabel = el("div", "new-doc__section-label");
    presetLabel.textContent = "Preset";
    this.thumbsHost = el("div", "new-doc__thumbnails");

    // Save Preset controls (shown only when Saved tab is active)
    const saveRow = el("div", "new-doc__save-preset-row");
    this.savePresetNameInput = el("input", "new-doc__save-preset-name");
    this.savePresetNameInput.type = "text";
    this.savePresetNameInput.placeholder = "Preset name…";
    this.savePresetNameInput.spellcheck = false;
    this.savePresetBtn = el("button", "new-doc__btn new-doc__save-preset-btn");
    this.savePresetBtn.type = "button";
    this.savePresetBtn.textContent = "Save Preset";
    saveRow.appendChild(this.savePresetNameInput);
    saveRow.appendChild(this.savePresetBtn);

    presetsCol.appendChild(presetLabel);
    presetsCol.appendChild(this.thumbsHost);
    presetsCol.appendChild(saveRow);
    body.appendChild(presetsCol);

    // Fields column
    const fields = el("div", "new-doc__fields");

    this.nameInput = this.mkTextRow(fields, "Name", "untitled");
    this.nameInput.addEventListener("input", () => {
      // free-form, no state-write needed; read at commit.
    });

    // Width row — contains the shared unit select so it aligns with Height
    const wRow = el("div", "new-doc__row");
    const wLab = el("label"); wLab.textContent = "Width";
    this.widthInput = el("input");
    this.widthInput.type = "text";
    this.widthInput.inputMode = "decimal";
    this.widthInput.spellcheck = false;
    this.unitSelect = el("select", "new-doc__unit");
    for (const u of ["px", "%", "in", "cm", "mm", "pt", "pc"] as Unit[]) {
      const opt = document.createElement("option");
      opt.value = u; opt.textContent = u;
      this.unitSelect.appendChild(opt);
    }
    wRow.appendChild(wLab);
    wRow.appendChild(this.widthInput);
    wRow.appendChild(this.unitSelect);
    fields.appendChild(wRow);

    // Orientation-swap button (between W and H rows, PS-style)
    const swapRow = el("div", "new-doc__row new-doc__swap-row");
    this.swapOrientationBtn = el("button", "new-doc__swap-btn");
    this.swapOrientationBtn.type = "button";
    this.swapOrientationBtn.title = "Swap width and height";
    this.swapOrientationBtn.setAttribute("aria-label", "Swap width and height");
    // Simple ⇅ icon using text — no external assets needed
    this.swapOrientationBtn.textContent = "⇅";
    swapRow.appendChild(this.swapOrientationBtn);
    fields.appendChild(swapRow);

    // Height
    const hRow = el("div", "new-doc__row");
    const hLab = el("label"); hLab.textContent = "Height";
    this.heightInput = el("input");
    this.heightInput.type = "text";
    this.heightInput.inputMode = "decimal";
    this.heightInput.spellcheck = false;
    hRow.appendChild(hLab);
    hRow.appendChild(this.heightInput);
    const hSpacer = el("span", "new-doc__unit-spacer");
    hRow.appendChild(hSpacer);
    fields.appendChild(hRow);

    // Resolution — input + unit dropdown (Pixels/Inch | Pixels/Centimeter)
    const dpiRow = el("div", "new-doc__row");
    const dpiLab = el("label"); dpiLab.textContent = "Resolution";
    this.dpiInput = el("input");
    this.dpiInput.type = "text";
    this.dpiInput.inputMode = "decimal";
    this.dpiInput.spellcheck = false;
    this.resUnitSelect = el("select", "new-doc__res-unit");
    for (const [val, label] of [["ppi", "Pixels/Inch"], ["ppcm", "Pixels/Cm"]] as Array<[ResolutionUnit, string]>) {
      const opt = document.createElement("option");
      opt.value = val; opt.textContent = label;
      this.resUnitSelect.appendChild(opt);
    }
    dpiRow.appendChild(dpiLab);
    dpiRow.appendChild(this.dpiInput);
    dpiRow.appendChild(this.resUnitSelect);
    fields.appendChild(dpiRow);

    // Color Mode (Bitmap, Grayscale, RGB, CMYK, Lab)
    const cmRow = el("div", "new-doc__row");
    const cmLab = el("label"); cmLab.textContent = "Color Mode";
    this.colorModeSelect = el("select");
    for (const [val, label] of [
      ["bitmap", "Bitmap"],
      ["grayscale", "Grayscale"],
      ["rgb", "RGB Color"],
      ["cmyk", "CMYK Color"],
      ["lab", "Lab Color"],
    ] as Array<[ColorMode, string]>) {
      const opt = document.createElement("option");
      opt.value = val; opt.textContent = label;
      this.colorModeSelect.appendChild(opt);
    }
    cmRow.appendChild(cmLab);
    cmRow.appendChild(this.colorModeSelect);
    fields.appendChild(cmRow);

    // Bit depth — options are populated dynamically by refreshBitDepthOptions()
    const bdRow = el("div", "new-doc__row");
    const bdLab = el("label"); bdLab.textContent = "Bit Depth";
    this.bitDepthSelect = el("select");
    bdRow.appendChild(bdLab);
    bdRow.appendChild(this.bitDepthSelect);
    fields.appendChild(bdRow);

    // Background
    const bgRow = el("div", "new-doc__row new-doc__bg");
    const bgLab = el("label"); bgLab.textContent = "Background";
    this.bgSelect = el("select");
    for (const [val, label] of [
      ["white", "White"],
      ["black", "Black"],
      ["transparent", "Transparent"],
      ["custom", "Custom..."],
    ] as Array<[BgChoice, string]>) {
      const opt = document.createElement("option");
      opt.value = val; opt.textContent = label;
      this.bgSelect.appendChild(opt);
    }
    this.bgSwatch = el("div", "new-doc__bg-swatch");
    bgRow.appendChild(bgLab);
    bgRow.appendChild(this.bgSelect);
    bgRow.appendChild(this.bgSwatch);
    fields.appendChild(bgRow);

    // ── Advanced disclosure ─────────────────────────────────────
    this.advancedHost = el("div", "new-doc__advanced");
    this.advancedToggle = el("button", "new-doc__advanced-toggle");
    this.advancedToggle.type = "button";
    this.advancedToggle.textContent = "Advanced";
    this.advancedToggle.addEventListener("click", () => {
      this.advancedHost.classList.toggle("is-open");
    });
    this.advancedHost.appendChild(this.advancedToggle);

    const advBody = el("div", "new-doc__advanced-body");

    // Color profile
    const cpRow = el("div", "new-doc__row");
    const cpLab = el("label"); cpLab.textContent = "Color Profile";
    this.colorProfileSelect = el("select");
    for (const profile of [
      "sRGB IEC61966-2.1", "Adobe RGB (1998)", "ProPhoto RGB", "Display P3",
      "Don't Color Manage This Document",
    ]) {
      const opt = document.createElement("option");
      opt.value = profile; opt.textContent = profile;
      this.colorProfileSelect.appendChild(opt);
    }
    cpRow.appendChild(cpLab);
    cpRow.appendChild(this.colorProfileSelect);
    advBody.appendChild(cpRow);

    // Pixel Aspect Ratio
    const parRow = el("div", "new-doc__row");
    const parLab = el("label"); parLab.textContent = "Pixel Aspect Ratio";
    this.pixelAspectInput = el("input");
    this.pixelAspectInput.type = "number";
    this.pixelAspectInput.min = "0.1";
    this.pixelAspectInput.max = "10";
    this.pixelAspectInput.step = "0.01";
    parRow.appendChild(parLab);
    parRow.appendChild(this.pixelAspectInput);
    advBody.appendChild(parRow);

    // Artboards checkbox + count
    const abRow = el("div", "new-doc__row");
    const abLab = el("label"); abLab.textContent = "Artboards";
    const abWrap = el("div");
    abWrap.style.display = "flex";
    abWrap.style.gap = "6px";
    abWrap.style.alignItems = "center";
    this.artboardsCheck = el("input");
    this.artboardsCheck.type = "checkbox";
    this.artboardsCount = el("input");
    this.artboardsCount.type = "number";
    this.artboardsCount.min = "1";
    this.artboardsCount.max = "100";
    this.artboardsCount.step = "1";
    this.artboardsCount.style.width = "60px";
    abWrap.appendChild(this.artboardsCheck);
    abWrap.appendChild(this.artboardsCount);
    abRow.appendChild(abLab);
    abRow.appendChild(abWrap);
    advBody.appendChild(abRow);

    this.advancedHost.appendChild(advBody);
    fields.appendChild(this.advancedHost);

    body.appendChild(fields);
    this.sheet.appendChild(body);

    // Actions
    const actions = el("div", "new-doc__actions");
    this.btnCancel = el("button", "new-doc__btn");
    this.btnCancel.type = "button";
    this.btnCancel.textContent = "Cancel";
    this.btnCreate = el("button", "new-doc__btn new-doc__btn--primary");
    this.btnCreate.type = "button";
    this.btnCreate.textContent = "Create";
    actions.appendChild(this.btnCancel);
    actions.appendChild(this.btnCreate);
    this.sheet.appendChild(actions);

    document.body.appendChild(this.root);

    this.wireEvents();
    this.built = true;
  }

  private mkTextRow(parent: HTMLElement, label: string, placeholder: string): HTMLInputElement {
    const row = el("div", "new-doc__row");
    const lab = el("label"); lab.textContent = label;
    const input = el("input");
    input.type = "text";
    input.spellcheck = false;
    input.placeholder = placeholder;
    row.appendChild(lab);
    row.appendChild(input);
    parent.appendChild(row);
    return input;
  }

  // ---------- Wire events ----------

  private wireEvents(): void {
    // Outside-click intentionally does NOT close (PS modal behaviour — #248).
    // The outsideHandler field is kept for destroy() symmetry but does nothing.
    this.outsideHandler = (_e: MouseEvent) => { /* no-op — PS modal */ };
    this.root.addEventListener("mousedown", this.outsideHandler);
    this.sheet.addEventListener("mousedown", (e) => e.stopPropagation());

    this.keyHandler = (e: KeyboardEvent) => {
      if (this.root.style.display === "none") return;
      if (this.colorPicker && (this.colorPicker as unknown as { resolveFn: unknown }).resolveFn) {
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        this.finish(null);
      } else if (e.key === "Enter") {
        const tgt = document.activeElement;
        if (tgt instanceof HTMLInputElement) {
          tgt.dispatchEvent(new Event("change"));
        }
        e.preventDefault();
        this.commit();
      } else if (e.key === "Tab") {
        trapFocus(e, this.sheet);
      }
    };
    window.addEventListener("keydown", this.keyHandler, true);

    this.btnCreate.addEventListener("click", () => this.commit());
    this.btnCancel.addEventListener("click", () => this.finish(null));

    this.widthInput.addEventListener("change", () => {
      const v = parseFloat(this.widthInput.value);
      if (Number.isFinite(v) && v > 0) {
        this.widthPx = Math.max(1, Math.round(unitToPixels(v, this.unit, this.dpi, this.widthPx)));
      }
      this.refreshDimensionInputs();
    });
    this.heightInput.addEventListener("change", () => {
      const v = parseFloat(this.heightInput.value);
      if (Number.isFinite(v) && v > 0) {
        this.heightPx = Math.max(1, Math.round(unitToPixels(v, this.unit, this.dpi, this.heightPx)));
      }
      this.refreshDimensionInputs();
    });

    // Orientation swap — swaps widthPx ↔ heightPx
    this.swapOrientationBtn.addEventListener("click", () => {
      const tmp = this.widthPx;
      this.widthPx = this.heightPx;
      this.heightPx = tmp;
      this.refreshDimensionInputs();
    });

    this.unitSelect.addEventListener("change", () => {
      const next = this.unitSelect.value as Unit;
      this.unit = next;
      this.refreshDimensionInputs();
    });

    this.dpiInput.addEventListener("change", () => {
      const displayed = parseFloat(this.dpiInput.value);
      if (Number.isFinite(displayed) && displayed > 0) {
        // Convert displayed value back to PPI for internal state
        this.dpi = this.resUnit === "ppcm" ? displayed * 2.54 : displayed;
      }
      this.refreshDpiInput();
      this.refreshDimensionInputs();
    });

    // Resolution unit switch: Pixels/Inch ↔ Pixels/Cm — convert displayed value
    this.resUnitSelect.addEventListener("change", () => {
      const next = this.resUnitSelect.value as ResolutionUnit;
      this.resUnit = next;
      this.refreshDpiInput();
    });

    this.colorModeSelect.addEventListener("change", () => {
      this.colorMode = this.colorModeSelect.value as ColorMode;
      this.refreshBitDepthOptions();
    });
    this.bitDepthSelect.addEventListener("change", () => {
      const v = parseInt(this.bitDepthSelect.value, 10);
      if (v === 1 || v === 8 || v === 16 || v === 32) this.bitDepth = v as BitDepth;
    });

    this.bgSelect.addEventListener("change", () => {
      const v = this.bgSelect.value as BgChoice;
      if (v === "custom") {
        this.openColorPicker();
      } else {
        this.bgChoice = v;
        this.refreshBg();
      }
    });

    this.colorProfileSelect.addEventListener("change", () => {
      this.colorProfile = this.colorProfileSelect.value;
    });
    this.pixelAspectInput.addEventListener("change", () => {
      const v = parseFloat(this.pixelAspectInput.value);
      if (Number.isFinite(v) && v > 0) this.pixelAspectRatio = v;
    });
    this.artboardsCheck.addEventListener("change", () => {
      this.artboardsOn = this.artboardsCheck.checked;
      this.artboardsCount.disabled = !this.artboardsOn;
    });
    this.artboardsCount.addEventListener("change", () => {
      const v = parseInt(this.artboardsCount.value, 10);
      if (Number.isFinite(v) && v >= 1) this.artboardsCountVal = v;
    });

    // Save Preset button — persists current dims + name to Saved tab
    this.savePresetBtn.addEventListener("click", () => {
      const rawName = this.savePresetNameInput.value.trim()
        || this.nameInput.value.trim()
        || `Untitled-${this.untitledCounter}`;
      const preset: Preset = {
        name: rawName,
        width: pixelsToUnit(this.widthPx, this.unit, this.dpi, this.widthPx),
        height: pixelsToUnit(this.heightPx, this.unit, this.dpi, this.heightPx),
        unit: this.unit,
        dpi: this.dpi,
      };
      savePreset(preset);
      this.savePresetNameInput.value = "";
      // Refresh the Saved tab if it's currently active
      if (this.category === "Saved") {
        this.populateThumbnails();
        this.refreshThumbActive();
      }
    });
  }

  private async openColorPicker(): Promise<void> {
    if (!this.colorPicker) this.colorPicker = new ColorPicker();
    const result = await this.colorPicker.open(this.customColor);
    if (result) {
      this.customColor = result;
      this.bgChoice = "custom";
    }
    this.refreshBg();
  }

  // ---------- Preset application ----------

  private populateThumbnails(): void {
    this.thumbsHost.innerHTML = "";
    this.thumbCards = [];
    const presets = this.presetsFor(this.category);
    if (presets.length === 0) {
      const empty = el("div", "new-doc__empty");
      empty.textContent = this.category === "Recent"
        ? "No recent documents yet. Create one to start a history."
        : "No saved presets. Save the current configuration to populate.";
      this.thumbsHost.appendChild(empty);
      return;
    }
    presets.forEach((p, idx) => {
      const card = el("div", "new-doc__thumb-card");
      card.dataset.idx = String(idx);
      const preview = el("div", "new-doc__thumb-preview");
      const aspect = el("div", "new-doc__thumb-aspect");
      const max = 50;
      const ratio = p.width / p.height;
      let w = max, h = max;
      if (ratio > 1) h = Math.max(8, Math.round(max / ratio));
      else if (ratio < 1) w = Math.max(8, Math.round(max * ratio));
      aspect.style.width = `${w}px`;
      aspect.style.height = `${h}px`;
      preview.appendChild(aspect);
      const label = el("div", "new-doc__thumb-label");
      label.textContent = p.name;
      const size = el("div", "new-doc__thumb-size");
      size.textContent = `${fmt(p.width, p.unit)} × ${fmt(p.height, p.unit)} ${p.unit}`;
      card.appendChild(preview);
      card.appendChild(label);
      card.appendChild(size);
      card.addEventListener("click", () => {
        this.selectedPresetIdx = idx;
        this.applyPreset(p);
        this.refreshAll();
      });
      this.thumbsHost.appendChild(card);
      this.thumbCards.push(card);
    });
  }

  private applyPreset(p: Preset): void {
    this.dpi = p.dpi;
    this.unit = p.unit;
    this.widthPx = Math.round(unitToPixels(p.width, p.unit, p.dpi));
    this.heightPx = Math.round(unitToPixels(p.height, p.unit, p.dpi));
  }

  // ---------- Refresh / commit ----------

  private refreshAll(): void {
    this.refreshTabs();
    this.populateThumbnails();
    this.refreshThumbActive();
    this.refreshDimensionInputs();
    this.refreshDpiInput();
    this.refreshBg();
    this.refreshAdvanced();
    this.refreshSavePresetRow();
  }

  private refreshTabs(): void {
    for (const [cat, btn] of this.tabEls.entries()) {
      btn.classList.toggle("is-active", cat === this.category);
    }
  }

  private refreshThumbActive(): void {
    this.thumbCards.forEach((card, idx) => {
      card.classList.toggle("is-active", idx === this.selectedPresetIdx);
    });
  }

  private refreshDimensionInputs(): void {
    this.unitSelect.value = this.unit;
    this.widthInput.value = fmt(pixelsToUnit(this.widthPx, this.unit, this.dpi, this.widthPx), this.unit);
    this.heightInput.value = fmt(pixelsToUnit(this.heightPx, this.unit, this.dpi, this.heightPx), this.unit);
  }

  private refreshDpiInput(): void {
    this.resUnitSelect.value = this.resUnit;
    // Display in selected resolution unit: ppcm = ppi / 2.54
    const displayed = this.resUnit === "ppcm" ? this.dpi / 2.54 : this.dpi;
    this.dpiInput.value = String(Math.round(displayed));
  }

  /**
   * Rebuild bit-depth option list based on colour mode (#248):
   *   Bitmap   → 1-bit only
   *   CMYK/Lab → 8-bit and 16-bit only (no 32-bit)
   *   Others   → 8 / 16 / 32
   * Clamps the current selection to a valid option.
   */
  private refreshBitDepthOptions(): void {
    type DepthEntry = [string, string];
    let entries: DepthEntry[];
    if (this.colorMode === "bitmap") {
      entries = [["1", "1 bit"]];
    } else if (this.colorMode === "cmyk" || this.colorMode === "lab") {
      entries = [["8", "8 bit"], ["16", "16 bit"]];
    } else {
      entries = [["8", "8 bit"], ["16", "16 bit"], ["32", "32 bit"]];
    }

    // Rebuild options
    this.bitDepthSelect.innerHTML = "";
    for (const [val, label] of entries) {
      const opt = document.createElement("option");
      opt.value = val; opt.textContent = label;
      this.bitDepthSelect.appendChild(opt);
    }

    // Clamp current bitDepth to available values
    const available = entries.map(([v]) => parseInt(v, 10)) as BitDepth[];
    if (!available.includes(this.bitDepth)) {
      this.bitDepth = available[0]!;
    }
    this.bitDepthSelect.value = String(this.bitDepth);
  }

  private refreshBg(): void {
    this.bgSelect.value = this.bgChoice;
    let css: string;
    switch (this.bgChoice) {
      case "white":       css = "#ffffff"; break;
      case "black":       css = "#000000"; break;
      case "transparent":
        this.bgSwatch.style.background = `
          linear-gradient(45deg, #ccc 25%, transparent 25%),
          linear-gradient(-45deg, #ccc 25%, transparent 25%),
          linear-gradient(45deg, transparent 75%, #ccc 75%),
          linear-gradient(-45deg, transparent 75%, #ccc 75%) #fff`;
        this.bgSwatch.style.backgroundSize = "8px 8px";
        this.bgSwatch.style.backgroundPosition = "0 0, 0 4px, 4px -4px, -4px 0";
        return;
      case "custom":
        css = `rgb(${this.customColor.r},${this.customColor.g},${this.customColor.b})`;
        break;
    }
    this.bgSwatch.style.background = css;
    this.bgSwatch.style.backgroundSize = "";
    this.bgSwatch.style.backgroundPosition = "";
  }

  private refreshAdvanced(): void {
    this.colorModeSelect.value = this.colorMode;
    this.refreshBitDepthOptions();   // rebuilds options and sets value
    this.colorProfileSelect.value = this.colorProfile;
    this.pixelAspectInput.value = String(this.pixelAspectRatio);
    this.artboardsCheck.checked = this.artboardsOn;
    this.artboardsCount.value = String(this.artboardsCountVal);
    this.artboardsCount.disabled = !this.artboardsOn;
  }

  /** Refresh the Save Preset row visibility (shown only on Saved tab). */
  private refreshSavePresetRow(): void {
    const row = this.savePresetBtn.closest(".new-doc__save-preset-row") as HTMLElement | null;
    if (row) row.style.display = this.category === "Saved" ? "" : "none";
  }

  /** Returns the current set of preset cards. Exposed for tests. */
  getThumbCards(): HTMLDivElement[] {
    return [...this.thumbCards];
  }

  /** Programmatically set category. Exposed for tests. */
  setCategory(cat: Category): void {
    if (!this.built) this.build();
    this.category = cat;
    this.selectedPresetIdx = 0;
    const presets = this.presetsFor(cat);
    if (presets[0]) this.applyPreset(presets[0]);
    this.refreshAll();
  }

  /** Open the Advanced disclosure (for testing / programmatic). */
  openAdvanced(): void {
    if (!this.built) this.build();
    this.advancedHost.classList.add("is-open");
  }

  private commit(): void {
    let bg: Background;
    switch (this.bgChoice) {
      case "white":       bg = "white"; break;
      case "black":       bg = "black"; break;
      case "transparent": bg = "transparent"; break;
      case "custom":      bg = { ...this.customColor }; break;
    }

    const nameRaw = this.nameInput.value.trim();
    const name = nameRaw.length > 0 ? nameRaw : `Untitled-${this.untitledCounter}`;
    this.untitledCounter += 1;

    // Background is "locked" in PS style when it is a solid fill (white, black,
    // or custom colour).  Transparent backgrounds start unlocked.
    const backgroundLocked = this.bgChoice !== "transparent";

    const result: NewDocumentResult = {
      name,
      width: Math.max(1, Math.round(this.widthPx)),
      height: Math.max(1, Math.round(this.heightPx)),
      dpi: this.dpi,
      background: bg,
      backgroundLocked,
      colorMode: this.colorMode,
      bitDepth: this.bitDepth,
      colorProfile: this.colorProfile,
      pixelAspectRatio: this.pixelAspectRatio,
      artboards: this.artboardsOn ? this.artboardsCountVal : 0,
    };

    // Persist as Recent preset (keyed by the entered dimensions in current unit)
    addRecent({
      name,
      width: pixelsToUnit(this.widthPx, this.unit, this.dpi, this.widthPx),
      height: pixelsToUnit(this.heightPx, this.unit, this.dpi, this.heightPx),
      unit: this.unit,
      dpi: this.dpi,
    });

    this.finish(result);
  }

  private finish(value: NewDocumentResult | null): void {
    if (!this.resolveFn) return;
    const fn = this.resolveFn;
    this.resolveFn = null;
    if (this.built) this.root.style.display = "none";
    // Restore focus to opener (#47).
    if (this.openerEl && "focus" in this.openerEl) {
      (this.openerEl as HTMLElement).focus();
    }
    this.openerEl = null;
    fn(value);
  }
}
